/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-16
 */

// Add third-party dependencies.
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// Add local dependencies.
import { Studio, StudioRole, StudioPermissions } from '../../types/studio';
import { authApi } from '../auth/authApi';

// Define the studio context state interface
interface StudioContextState {
  currentStudio: Studio | null;
  userRole: StudioRole | null;
  permissions: StudioPermissions | null;
  recentStudios: Studio[];
  isLoading: boolean;
  error: string | null;
  lastSwitched: string | null;
}

// Define the initial state
const initialState: StudioContextState = {
  currentStudio: null,
  userRole: null,
  permissions: null,
  recentStudios: [],
  isLoading: false,
  error: null,
  lastSwitched: null,
};

// Create the studio context slice
const studioContextSlice = createSlice({
  name: 'studioContext',
  initialState,
  reducers: {
    setCurrentStudio: (
      state,
      action: PayloadAction<{
        studio: Studio;
        role: StudioRole | null;
        permissions: StudioPermissions | null;
      }>
    ) => {
      /* eslint-disable no-param-reassign */
      state.currentStudio = action.payload.studio;
      state.userRole = action.payload.role;
      state.permissions = action.payload.permissions;
      state.lastSwitched = new Date().toISOString();
      state.error = null;

      // Add to recent studios if not already there
      const existingIndex = state.recentStudios.findIndex(
        s => s.id === action.payload.studio.id
      );
      if (existingIndex >= 0) {
        state.recentStudios.splice(existingIndex, 1);
      }
      state.recentStudios.unshift(action.payload.studio);

      // Keep only the 5 most recent studios
      if (state.recentStudios.length > 5) {
        state.recentStudios = state.recentStudios.slice(0, 5);
      }
      /* eslint-enable no-param-reassign */
    },

    clearCurrentStudio: state => {
      /* eslint-disable no-param-reassign */
      state.currentStudio = null;
      state.userRole = null;
      state.permissions = null;
      state.lastSwitched = null;
      state.error = null;
      /* eslint-enable no-param-reassign */
    },

    updateCurrentStudio: (state, action: PayloadAction<Studio>) => {
      /* eslint-disable no-param-reassign */
      if (state.currentStudio && state.currentStudio.id === action.payload.id) {
        state.currentStudio = action.payload;

        // Update in recent studios as well
        const recentIndex = state.recentStudios.findIndex(
          s => s.id === action.payload.id
        );
        if (recentIndex >= 0) {
          state.recentStudios[recentIndex] = action.payload;
        }
      }
      /* eslint-enable no-param-reassign */
    },

    setRecentStudios: (state, action: PayloadAction<Studio[]>) => {
      /* eslint-disable no-param-reassign */
      state.recentStudios = action.payload.slice(0, 5);
      /* eslint-enable no-param-reassign */
    },

    setLoading: (state, action: PayloadAction<boolean>) => {
      /* eslint-disable no-param-reassign */
      state.isLoading = action.payload;
      /* eslint-enable no-param-reassign */
    },

    setError: (state, action: PayloadAction<string | null>) => {
      /* eslint-disable no-param-reassign */
      state.error = action.payload;
      state.isLoading = false;
      /* eslint-enable no-param-reassign */
    },

    clearError: state => {
      /* eslint-disable no-param-reassign */
      state.error = null;
      /* eslint-enable no-param-reassign */
    },

    resetStudioContext: () => initialState,
  },
  extraReducers: builder => {
    builder
      // Handle auth logout to clear studio context
      .addMatcher(
        authApi.endpoints.sessionLogout.matchFulfilled,
        () => initialState
      )
      // Handle manual logout action to clear studio context
      .addMatcher(
        action => action.type === 'auth/logout',
        () => initialState
      );
  },
});

// Export actions
export const {
  setCurrentStudio,
  clearCurrentStudio,
  updateCurrentStudio,
  setRecentStudios,
  setLoading,
  setError,
  clearError,
  resetStudioContext,
} = studioContextSlice.actions;

// Export reducer
export default studioContextSlice.reducer;

// Export selectors
export const selectCurrentStudio = (state: {
  studioContext: StudioContextState;
}) => state.studioContext.currentStudio;

export const selectUserRole = (state: { studioContext: StudioContextState }) =>
  state.studioContext.userRole;

export const selectStudioPermissions = (state: {
  studioContext: StudioContextState;
}) => state.studioContext.permissions;

export const selectRecentStudios = (state: {
  studioContext: StudioContextState;
}) => state.studioContext.recentStudios;

export const selectStudioContextLoading = (state: {
  studioContext: StudioContextState;
}) => state.studioContext.isLoading;

export const selectStudioContextError = (state: {
  studioContext: StudioContextState;
}) => state.studioContext.error;

export const selectLastSwitched = (state: {
  studioContext: StudioContextState;
}) => state.studioContext.lastSwitched;

// Computed selectors
export const selectHasStudioAccess =
  (studioId: string) => (state: { studioContext: StudioContextState }) =>
    state.studioContext.recentStudios.some(studio => studio.id === studioId) ||
    state.studioContext.currentStudio?.id === studioId;

export const selectCanAccessFeature =
  (feature: keyof StudioPermissions) =>
  (state: { studioContext: StudioContextState }) =>
    state.studioContext.permissions?.[feature] ?? false;
